import React, { useEffect, useState, useRef  } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import useAppLayout from './hooks/useAppLayout';
import API_URL from './api/config';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReactModal from 'react-modal';



// --- Global OrgChart script loader (only loads once for the application) ---
let orgChartScriptLoaded = false;
let orgChartLoadPromise = null;

const loadOrgChartScript = (src) => {
  if (orgChartScriptLoaded) {
    return Promise.resolve();
  }
  if (orgChartLoadPromise) {
    return orgChartLoadPromise;
  }

  orgChartLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => {
      orgChartScriptLoaded = true;
      resolve();
    };
    script.onerror = reject;
    document.body.appendChild(script);
  });

  return orgChartLoadPromise;
};
// --- End Global OrgChart script loader ---


function PrintTreePreview() {
  const { menu, toggleMobileMenu } = useAppLayout();

  const navigate = useNavigate();
  
   const chartContainerRef = useRef(null);
    const chartInstanceRef = useRef(null);
  
    const queryClient = useQueryClient();
  
    const getToken = () => localStorage.getItem('token');
  
  
   // Query to fetch family tree data
  const { data: familyTreeData, isLoading: isLoadingFamilyTree, isError: isErrorFamilyTree } = useQuery({
      queryKey: ['familyTree'],
      queryFn: () =>
        axios
          .get(`${API_URL}/family-tree`, {
            headers: {
              Authorization: `Bearer ${getToken()}`,
            },
          })
          .then((res) => res.data),
      staleTime: 0,
      cacheTime: Infinity,
    });
  
  
  
     // Load OrgChart script when data is available
     useEffect(() => {
      if (!familyTreeData) return; // Wait for data to be available
  
      loadScript(`${process.env.PUBLIC_URL}/orgchart/orgchart.js`)
        .then(() => {
          initializeOrgChart(familyTreeData);
          //console.log("family TreeData:", familyTreeData);
        })
        .catch((error) => {
          console.error('Failed to load OrgChart script:', error);
        });
  
      return () => {
        if (chartInstanceRef.current) {
          chartInstanceRef.current.destroy();
        }
      };
    }, [familyTreeData]); // Runs only when familyTreeData is updated
  
  
    const loadScript = (src) => {
      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
      });
    };
  
    const chartBgColor = "#EBF4ED";
  
   
    const initializeOrgChart = (data) => {
      if (!window.OrgChart) return;
      const isMobile = window.innerWidth <= 768;
      const OrgChart = window.OrgChart;
  
      OrgChart.templates.family_template = Object.assign({}, OrgChart.templates.ana);
      OrgChart.templates.family_template.size = [86, 86];
      OrgChart.templates.family_template.plus = '';
      OrgChart.templates.family_template.minus = '';
      OrgChart.templates.family_template.rippleRadius = 40;
      OrgChart.templates.family_template.name = '<text style="font-size: 12px;" fill="#000000" x="43" y="100" text-anchor="middle">{val}</text>';
      OrgChart.templates.family_template.title = '<text style="font-size: 12px;" fill="#aeaeae" x="43" y="115" text-anchor="middle">{val}</text>';
      OrgChart.templates.family_template.img = '<clipPath id="{randId}"><circle cx="43" cy="43" r="38.5"></circle></clipPath></circle><image preserveAspectRatio="xMidYMid slice" clip-path="url(#{randId})" xlink:href="{val}" x="3" y="3" width="80" height="78"></image>';
      OrgChart.templates.family_template.node = '<circle stroke-width="3" fill="none" stroke="#FF1493" cx="43" cy="43" r="41.5"></circle>';
  
  
      OrgChart.templates.family_template_blue = Object.assign({}, OrgChart.templates.family_template);
      OrgChart.templates.family_template_blue.node = '<circle stroke-width="3" fill="none" stroke="#039BE5" cx="43" cy="43" r="41.5"></circle>';
  
      const chart = new OrgChart(chartContainerRef.current, {
        template: 'family_template',
        
        enableSearch: false,
        levelSeparation: isMobile ? 60 : 100,
        siblingSeparation: isMobile ? 60 : 100,
        scaleInitial: isMobile ? OrgChart.match.boundary : 1,
        scaleMin: 0.5,
        scaleMax: 3,
       
        nodeBinding: {
          name: 'name',
          title: 'title',
          img: 'img',
        },
        tags: {
          blue: { template: 'family_template_blue' },
          partner: { template: 'family_template' },
        },
  
        nodeMouseClick: OrgChart.action.none, // override default popup
  
      });
  
    
  
      chart.on('render-link', (sender, args) => {
        if (args.cnode.ppid !== undefined) {
          args.html += '<use xlink:href="#baby" x="' + args.p.xa + '" y="' + args.p.ya + '"/>';
        }
      });
  
      chart.on('redraw', function () {
        const chartElement = document.querySelector('#tree svg');
        if (chartElement) {
          chartElement.style.touchAction = 'pan-x pan-y';
        }
      });
  
  
      chart.load(data);
      chartInstanceRef.current = chart;
  
  
    };
  
  
    const { data: treeOrderDetails, isLoadingTb, isErrorTb } = useQuery({
      queryKey: ["treeOrderDetails"],
      queryFn: () =>
        axios
          .get(`${API_URL}/tree-order-details-api`, { withCredentials: true })
          .then((res) => res.data),
      
    });
  
   const [viewOrderDetailsModalOpen, setViewOrderDetailsModalOpen] = useState(false);
  
    
    const [loading, setLoading] = useState(false);
  
    const handleCompleteOrder = async () => {
      setLoading(true);
    
      try {
        const response = await axios.post(
          `${API_URL}/complete-tree-order-api`,
          {}, // no body needed
          { withCredentials: true } //config goes here
        );

        if (response.data.success) {
             const verifyCode = response.data.order_code;
        navigate(`/tree-order-payment-web?verifyCode=${verifyCode}`);
                    
        } else {
        toast.error(response.data.message || "Something went wrong");
        }
    
       
      } catch (error) {
        toast.error("Error sending details");
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
   
  
    if (isLoadingFamilyTree) return( 
      <div className="loadingspinnerDiv">
    <div className="loadingspinner"></div>
  </div>
       );
    
    if (isErrorFamilyTree) return <p>Error loading family tree data.</p>;
  
  


  return (
    <div className={`main-wrapper ${menu ? 'slide-nav': ''}`}>
            <Header onMenuClick={(value) => toggleMobileMenu()} />
            <Sidebar />
        <ToastContainer />

        <div className='page-wrapper'>
          <div className='content container-fluid'>


        
                  
                  <div className='row'>
                  <div className='col-2 col-md-2'>
                  <Link  to='/print-tree-order' className='btn btn-default-primary'>
                          <i className="fa fa-arrow-left" style={{ color: '#184A2C', fontSize: '20px',  }}></i>
                          </Link>
                          </div>
        
                        <div className='col-10 col-md-10'>
                        </div>
                  </div>
        
        
                  <div className='row'>
                  <div className='col-md-12'>
                 
                  <div
          style={{
            position: "relative",
            width: "100%",
            height: "600px",
            backgroundColor: treeOrderDetails?.background_color || "white",
          }}
        >
          {/* Header overlay */}
          {treeOrderDetails?.header && (
            <h3
              style={{
                position: "absolute",
                top: "10px",
                left: "50%",
                width: "80%",
                textAlign: "center",
                transform: "translateX(-50%)",
                background: "rgba(255,255,255,0.7)",
                padding: "5px 15px",
                borderRadius: "2px",
                zIndex: 10,
                
              }}
            >
              {treeOrderDetails.header}
            </h3>
          )}
        
          {/* OrgChart container */}
          <div
            id="tree"
            ref={chartContainerRef}
            style={{ width: "100%", height: "100%" }}
          ></div>
        
          <div className='row' style={{position: "absolute",   bottom: "10%",
                   zIndex: 10, width: '100%'}}>
               <div className='col-4 col-md-4'>
        
            
              <button onClick={() => setViewOrderDetailsModalOpen(true)} 
              style={{marginLeft: '10%'}}
              class="btn btn-primary">View Order Details</button>
        
              </div>

              <div className='col-4 col-md-4'>

              </div>
              <div className='col-4 col-md-4'>
              <button onClick={handleCompleteOrder}  className='btn btn-primary'   disabled={loading}>
                    
                    {loading ? 'Completing...' : 'Complete Order'} 
                    
                    </button>
              </div>
          </div>
         
         
        
        </div>
        
                  
                  </div>
                  </div>
        
        
        
                  <ReactModal isOpen={viewOrderDetailsModalOpen} onRequestClose={() => setViewOrderDetailsModalOpen(false)} 
                                                className="Modal" overlayClassName="Overlay"
                                              >
                                  <div className="modal-header">
                                    <h5 className="modal-title">Tree Order Details</h5>
                                    <button type="button" className="btn-close modalCloseButton" onClick={() => setViewOrderDetailsModalOpen(false)}></button>
                                  </div>
                      
                                  <div className="modal-body">
                  
                                  {isLoadingTb && <p>Loading order details...</p>}
        {isErrorTb && <p className="text-danger">Error loading order details</p>}
        
        {treeOrderDetails ? (
          <div className="row">
            <h5><strong>Header:</strong> {treeOrderDetails.header}</h5>
            <p><strong>Tree Size:</strong> {treeOrderDetails.tree_size_name}</p>
            <p><strong>No. of Copies:</strong> {treeOrderDetails.number_of_copies}</p>
            <p>
              <strong>Background:</strong>{" "}
              <span
                style={{
                  display: "inline-block",
                  width: "20px",
                  height: "20px",
                  backgroundColor: treeOrderDetails.background_color,
                  border: "1px solid #ccc",
                  marginLeft: "5px",
                }}
              ></span>
            </p>
            <p><strong>Names:</strong> {treeOrderDetails.full_names}</p>
            <p><strong>Phone:</strong> {treeOrderDetails.phone_number}</p>
            <p><strong>Email:</strong> {treeOrderDetails.email}</p>
            <p><strong>Pickup Location:</strong> {treeOrderDetails.pickup_location_name}</p>
            <p><strong>Total Amount:</strong> KES {treeOrderDetails.total_amount}</p>
            <p><strong>Date:</strong> {new Date(treeOrderDetails.date_time).toLocaleString()}</p>
          </div>
        ) : (
          <p>No active tree order found.</p>
        )}
                            
                                </div>
                            
                                <div className="modal-footer">
                                            
                                    
                                  </div>
                                    </ReactModal>
                              
            

          </div>
        </div>
      </div>
    
  );
}

export default PrintTreePreview;
